// Taps from one tracker, applied one at a time, in the order they were made.
//
// BUG-5 in the 2026-09-17 dev test: a tap that landed while the previous one was
// still saving was ignored (`if (busy.current) return;`). 8 of 130 fast taps on
// a counter were lost that way, with nothing on screen to say so, and the three
// counter categories are where the study's key numbers come from.
//
// Now a tap waits its turn behind the one before it, and carries the moment it
// was made, not the moment its turn came, so block times stay true. The rule
// that nothing moves on screen before it is saved still holds tap by tap:
// `commit` runs only once `persist` has resolved.

import { withLock } from "./serial.ts";
import { tap as applyTap, type TapOutcome, type TrackerState } from "./tracker.ts";
import type { CategoryId } from "./categories.ts";

export type Applied = Exclude<TapOutcome, { kind: "ignored" }>;

export interface TapperDeps {
  /** One queue per session. Never the key mutateSession locks on, or a tap
      would wait on its own save. */
  key: string;
  /** The state the next tap is computed from: whatever the last saved tap left. */
  current: () => TrackerState;
  newId: () => string;
  persist: (next: TrackerState, outcome: Applied, at: number) => Promise<void>;
  commit: (next: TrackerState, outcome: Applied) => void;
}

export function createTapper(d: TapperDeps) {
  /** A tap whose save fails rejects, and the taps behind it still run, from the
      state as it was before the failed one: that tap never happened. */
  const tap = (categoryId: CategoryId, at: number): Promise<TapOutcome> =>
    withLock(d.key, async () => {
      const { state: next, outcome } = applyTap(d.current(), categoryId, at, d.newId);
      if (outcome.kind === "ignored") return outcome;
      await d.persist(next, outcome, at);
      d.commit(next, outcome);
      return outcome;
    });

  /** Runs once every tap already queued has settled. Ending a session goes
      through here, so it can never close a block ahead of a tap already made. */
  const after = <T>(fn: () => Promise<T>): Promise<T> => withLock(d.key, fn);

  return { tap, after };
}
